import { ChangeEvent, useState } from "react";
import { getItemPopupList } from "src/apis/itemApi";
import { BRDiv } from "src/components/common/common";
import { CodeForm } from "src/components/common/form/CodeForm";
import { DropDownForm } from "src/components/common/form/DropdownForm";
import { ImageForm } from "src/components/common/form/ImageForm";
import { InputForm } from "src/components/common/form/input/InputForm";
import { SingleDateForm } from "src/components/common/form/SingleDateForm";
import { SysCodeDto } from "src/dtos/code/sysCodeDto";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import useUiuxStore from "src/store/useUiuxStore";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";
import { MST4040_R01Dto } from "./Dto_MST4040_R01";
import { useMST4040_R01Store } from "./Store_MST4040_R01";
import { formatDate } from "src/utils/string/stringUtil";

interface Props {
	defaultMST4040_R01Row: MST4040_R01Dto;
}

export const Info_MST4040_R01 = ({ defaultMST4040_R01Row }: Props) => {
	const { editMST4040_R01, setEditMST4040_R01, selectedMST4040_R01 } = useMST4040_R01Store();
	const { moldTypeList, moldClassList, eqmStatusList, yesNoList } = useCommonCodeStore();
	const { setCodeSearchModalProps } = useUiuxStore();

	const [makeDtPickerVisible, setMakeDtPickerVisible] = useState(false);
	const [makeDtPickerPos, setMakeDtPickerPos] = useState<{ top: number; left: number; height: number }>({
		top: 0,
		left: 0,
		height: 0,
	});
	const [purchaseDtPickerVisible, setPurchaseDtPickerVisible] = useState(false);
	const [purchaseDtPickerPos, setPurchaseDtPickerPos] = useState<{ top: number; left: number; height: number }>({
		top: 0,
		left: 0,
		height: 0,
	});

	const itemCdFocusId = 'itemCdInput_MST4040_R01';

	const handleChange = (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;
		const numericFields = ['cavity', 'purchaseAmt', 'shotLimit', 'currentShot', 'cleanCycle'];

		setEditMST4040_R01({
			...editMST4040_R01,
			[name]: numericFields.includes(name) ? Number(value) || 0 : value,
		});
	};

	const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
		{
			fieldName: 'itemCd',
			category: '품목',
			focusId: itemCdFocusId,
			responseMapping: {
				itemCd: 'itemCd',
				itemNm: 'itemNm',
			},
			searchFunction: async (code: string) => {
				const response = await getItemPopupList(code, '', '', '');
				return response.data.data.find((value) => value.itemCd === code);
			},
			extraFilter: () => ({
				itemCd: editMST4040_R01?.itemCd || '',
				itemNm: editMST4040_R01?.itemNm || '',
			}),
		},
	];

	const { getFieldHandlers } = useInfoFormEvents({
		editData: editMST4040_R01,
		setEditData: setEditMST4040_R01,
		defaultData: defaultMST4040_R01Row,
		infoFieldConfigs,
		setCodeSearchModalProps,
		formName: 'MST4040_R01',
	});

	const inputWrapperStyle = {
		wrapperStyle: "w-[250px] min-w-[250px] mr-[10px] mb-[5px]",
		titleStyle: "w-[100px] min-w-[100px]",
		inputStyle: "w-[150px] min-w-[150px]",
	};

	const codeWrapperStyle = {
		wrapperStyle: "w-[500px] min-w-[500px] mr-[10px] mb-[5px]",
		titleStyle: "w-[100px] min-w-[100px]",
		codeInputStyle: "w-[150px] min-w-[150px]",
		custNmInputStyle: "w-[255px] min-w-[255px]",
	};

	const moldImageWrapperStyle = {
		wrapperStyle: "w-[300px] h-[250px]",
	};

	return (
		<div className="w-full h-full overflow-y-auto flex border border-gray-300">
			<div className="w-[300px] h-full flex flex-wrap content-start gap-[12px] p-[12px]">
				<ImageForm
					title="금형사진"
					image={editMST4040_R01.moldImage}
					onImageChange={(moldImage) => setEditMST4040_R01({ ...editMST4040_R01, moldImage })}
					wrapperOption={moldImageWrapperStyle}
					previewEnabled
				/>
			</div>
			<div className="w-[75%] h-full flex flex-wrap content-start p-[12px]">
				<InputForm
					title="금형코드"
					value={editMST4040_R01.moldCd}
					editable={selectedMST4040_R01 === undefined}
					name="moldCd"
					onChange={handleChange}
					wrapperOption={inputWrapperStyle}
				/>
				<InputForm
					title="금형명"
					value={editMST4040_R01.moldNm}
					editable={true}
					name="moldNm"
					onChange={handleChange}
					wrapperOption={inputWrapperStyle}
				/>
				<BRDiv />
				<SingleDateForm
					title="제작일자"
					value={editMST4040_R01?.makeDt ? new Date(editMST4040_R01.makeDt) : new Date()}
					setValue={(value: Date | undefined) =>
						setEditMST4040_R01({
							...defaultMST4040_R01Row,
							...editMST4040_R01,
							makeDt: value ? formatDate(value) : '',
						})
					}
					editable={true}
					visible={makeDtPickerVisible}
					setVisible={setMakeDtPickerVisible}
					onChange={handleChange}
					pickerPos={makeDtPickerPos}
					setPickerPos={setMakeDtPickerPos}
					wrapperOption={inputWrapperStyle}
				/>
				<InputForm
					title="제작사"
					value={editMST4040_R01.makerNm}
					editable={true}
					name="makerNm"
					onChange={handleChange}
					wrapperOption={inputWrapperStyle}
				/>
				<BRDiv />
				<InputForm
					title="현재타수"
					value={editMST4040_R01.currentShot}
					editable={true}
					name="currentShot"
					onChange={handleChange}
					wrapperOption={inputWrapperStyle}
				/>
				<InputForm
					title="한계타수"
					value={editMST4040_R01.shotLimit}
					editable={true}
					name="shotLimit"
					onChange={handleChange}
					wrapperOption={inputWrapperStyle}
				/>
				<BRDiv />
				<DropDownForm<SysCodeDto>
					title="금형유형"
					value={editMST4040_R01.moldType}
					data={moldTypeList}
					editable={true}
					name="moldType"
					initLabelLess={true}
					onChange={handleChange}
					wrapperOption={inputWrapperStyle}
				/>
				<DropDownForm<SysCodeDto>
					title="금형구분"
					value={editMST4040_R01.moldClass}
					data={moldClassList}
					editable={true}
					name="moldClass"
					initLabelLess={true}
					onChange={handleChange}
					wrapperOption={inputWrapperStyle}
				/>
				<BRDiv />
				<SingleDateForm
					title="구입일자"
					value={editMST4040_R01?.purchaseDt ? new Date(editMST4040_R01.purchaseDt) : new Date()}
					setValue={(value: Date | undefined) =>
						setEditMST4040_R01({
							...defaultMST4040_R01Row,
							...editMST4040_R01,
							purchaseDt: value ? formatDate(value) : '',
						})
					}
					editable={true}
					visible={purchaseDtPickerVisible}
					setVisible={setPurchaseDtPickerVisible}
					onChange={handleChange}
					pickerPos={purchaseDtPickerPos}
					setPickerPos={setPurchaseDtPickerPos}
					wrapperOption={inputWrapperStyle}
				/>
				<InputForm
					title="구입금액"
					value={editMST4040_R01.purchaseAmt}
					editable={true}
					name="purchaseAmt"
					onChange={handleChange}
					wrapperOption={inputWrapperStyle}
				/>
				<BRDiv />
				<CodeForm
					label="생산품목"
					value={editMST4040_R01?.itemCd || ""}
					custNmValue={editMST4040_R01?.itemNm || ""}
					editable={true}
					handleFoucsId={itemCdFocusId}
					name="itemCd"
					wrapperOption={codeWrapperStyle}
					handleChange={getFieldHandlers('itemCd').onChange}
					onDoubleClick={getFieldHandlers('itemCd').onDoubleClick}
					onKeyDown={getFieldHandlers('itemCd').onEnterKeyDown}
				/>
				<BRDiv />
				<InputForm
					title="Cavity"
					value={editMST4040_R01.cavity}
					editable={true}
					name="cavity"
					onChange={handleChange}
					wrapperOption={inputWrapperStyle}
				/>
				<InputForm
					title="세척주기"
					value={editMST4040_R01.cleanCycle}
					editable={true}
					name="cleanCycle"
					onChange={handleChange}
					wrapperOption={inputWrapperStyle}
				/>
				<BRDiv />
				<InputForm
					title="비고"
					value={editMST4040_R01.remark}
					editable={true}
					wrapperOption={{
						wrapperStyle: "w-[510px] min-w-[510px] mr-[10px] mb-[10px]",
						titleStyle: "w-[100px] min-w-[100px]",
						inputStyle: "w-[410px] min-w-[410px]",
					}}
					name="remark"
					onChange={handleChange}
				/>
			</div>
		</div>
	);
};
